// Candidate "Min arbetsplats" — shows where the candidate is employed (org + unit),
// since when, and who else is in the unit. Empty state when not yet employed.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Building, Calendar, Users, GraduationCap, Briefcase } from 'lucide-react';
import { workplaceApi, type Workplace } from '@/lib/api/workplaceApi';
import { LoadingState, EmptyState } from '@/components/custom-ui/empty-state';
import { OutlineButton } from '@/components/custom-ui/action-button';
import { IconChip } from '@/components/custom-ui/IconChip';
import { EntityAvatar } from '@/components/custom-ui/EntityAvatar';
import { UserAvatar } from '@/components/custom-ui/UserAvatar';
import { NEXILINK_CARD_WHITE_ELEVATED, NEXILINK_SPACING } from '@/lib/nexilink-card-styles';

const MyWorkplacePage: React.FC = () => {
    const navigate = useNavigate();
    const [workplaces, setWorkplaces] = useState<Workplace[] | null>(null);

    useEffect(() => {
        let cancelled = false;
        workplaceApi.getMyWorkplace().then((w) => { if (!cancelled) setWorkplaces(w); });
        return () => { cancelled = true; };
    }, []);

    if (workplaces === null) {
        return <LoadingState message="Laddar arbetsplats..." />;
    }

    if (workplaces.length === 0) {
        return (
            <div className={NEXILINK_SPACING}>
                <EmptyState
                    icon={Building2}
                    title="Du är inte anställd ännu"
                    description="När du anställs via en jobbansökan eller en accepterad inbjudan visas din arbetsplats här. Hitta din nästa roll bland lediga tjänster."
                    action={{ label: 'Sök jobb', icon: Briefcase, onClick: () => navigate('/dashboard/jobs') }}
                />
            </div>
        );
    }

    return (
        <div className={NEXILINK_SPACING}>
            {workplaces.map((w) => (
                <div key={`${w.organisation_id}-${w.unit_id}`} className={NEXILINK_SPACING}>
                    {/* Workplace header */}
                    <div className={`${NEXILINK_CARD_WHITE_ELEVATED} p-6`}>
                        <div className="flex items-start gap-4">
                            <EntityAvatar icon={Building2} size="lg" orgName={w.organisation_name} orgLogoUrl={w.organisation_logo_url} />
                            <div className="flex-1 min-w-0">
                                {w.title && <p className="text-sm font-medium text-[#1A4D4F]">{w.title}</p>}
                                <h2 className="text-lg font-semibold text-gray-900">{w.organisation_name || 'Organisation'}</h2>
                                {w.unit_name && (
                                    <p className="text-sm text-gray-500 flex items-center gap-1.5 mt-0.5">
                                        <Building className="w-4 h-4 text-gray-400" /> {w.unit_name}
                                    </p>
                                )}
                                <p className="text-xs text-gray-400 flex items-center gap-1.5 mt-1">
                                    <Calendar className="w-4 h-4" /> Anställd sedan {new Date(w.started_at).toLocaleDateString('sv-SE')}
                                </p>
                            </div>
                            <OutlineButton
                                icon={GraduationCap}
                                onClick={() => navigate('/dashboard/my-onboarding')}
                                className="flex-shrink-0"
                            >
                                Min onboarding
                            </OutlineButton>
                        </div>
                    </div>

                    {/* Colleagues */}
                    <div className={`${NEXILINK_CARD_WHITE_ELEVATED} p-6`}>
                        <div className="flex items-center gap-3 mb-4">
                            <IconChip icon={Users} />
                            <h3 className="text-lg font-semibold text-gray-900">
                                Kollegor <span className="text-gray-400 font-normal">({w.colleagues.length})</span>
                            </h3>
                        </div>
                        {w.colleagues.length === 0 ? (
                            <p className="text-sm text-gray-500">Inga andra kollegor i din enhet ännu.</p>
                        ) : (
                            <div className="divide-y divide-gray-50">
                                {w.colleagues.map((c, i) => (
                                    <div key={`${c.name}-${i}`} className="flex items-center gap-3 py-3">
                                        <UserAvatar name={c.name} pictureUrl={c.picture_url} size="sm" />
                                        <span className="text-sm font-semibold text-gray-900 flex-1">{c.name}</span>
                                        {c.role === 'employer' && (
                                            <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">Arbetsgivare</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default MyWorkplacePage;
